import React, { useEffect, useRef, useState } from 'react';
import { DropDownListComponent } from '@syncfusion/ej2-react-dropdowns';
import { GridComponent, ColumnsDirective, ColumnDirective, Inject, Sort } from '@syncfusion/ej2-react-grids';
import { type CrudFormProps } from '../../components/CrudPage';
import { type ProcessingLineSetupTimeDto } from '../../api';
import { ResourceTypeControllerService, type ResourceTypeDto } from '../../api';
import { ResourceControllerService, type ResourceDto } from '../../api';
import { MaterialControllerService, type MaterialDto } from '../../api';
import { FormDialog } from '../../components/FormDialog';
import { DurationInput } from '../../components/DurationInput';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '../../utils/zodResolver';
import { z } from 'zod';
import type { FormState, Resolver, UseFormSetValue, UseFormTrigger, UseFormWatch } from 'react-hook-form';
import type { TFunction } from 'i18next';

type SetupTimeResourceRequirementForm = {
  resourceTypeUuid?: string;
  resources?: { resourceUuid: string; quantity: number }[];
};

// Public contract used by the page and CrudPage: DTO shape + generated `id` for
// the grid key. The form converts between this shape and the internal form state.
export type ProcessingLineSetupTimeWithId = ProcessingLineSetupTimeDto & { id: string };

// Internal form state shape used by react-hook-form. `resourceRequirement` carries
// the form-friendly representation (resourceUuid + quantity required per row)
// instead of the DTO's optional-field ResourceRequirementDto.
type ProcessingLineSetupTimeFormState = Omit<ProcessingLineSetupTimeDto, 'resourceRequirement'> & {
  id: string;
  resourceRequirement?: SetupTimeResourceRequirementForm | null;
};

// Resource types for setup requirement: only non-single resources
const nonSingleResourceTypes = (rts: ResourceTypeDto[]) =>
  rts.filter(rt => rt.singleResource === false || rt.singleResource == null);

type SetupResourceRequirementBlockProps = {
  watch: UseFormWatch<ProcessingLineSetupTimeFormState>;
  setValue: UseFormSetValue<ProcessingLineSetupTimeFormState>;
  allResourceTypes: ResourceTypeDto[];
  resources: ResourceDto[];
  t: TFunction;
};

const SetupResourceRequirementBlock: React.FC<SetupResourceRequirementBlockProps> = ({
  watch,
  setValue,
  allResourceTypes,
  resources,
  t
}) => {
  const resourceTypesForRequirement = nonSingleResourceTypes(allResourceTypes);
  const currentReq = watch('resourceRequirement');
  const reqResourceTypeId = currentReq?.resourceTypeUuid ?? '';
  // Snapshot of existing resources at mount / resource-type-change — stored in ref to avoid feedback loop
  const initialResourcesRef = useRef<{ resourceUuid: string; quantity: number }[]>([]);

  const [gridRows, setGridRows] = useState<{ resource: ResourceDto }[]>([]);
  const [selectedUuids, setSelectedUuids] = useState<Set<string>>(new Set());
  const gridRef = useRef<GridComponent | null>(null);
  // Quantities keyed by resource UUID — managed in ref to avoid triggering re-renders
  const quantityMapRef = useRef<Map<string, number>>(new Map());

  useEffect(() => {
    if (!reqResourceTypeId || resources.length === 0) {
      setGridRows([]);
      return;
    }
    const existing = currentReq?.resources ?? [];
    initialResourcesRef.current = existing;

    const existingMap = new Map<string, { resourceUuid: string; quantity: number }>();
    existing.forEach(rr => {
      if (rr.resourceUuid) existingMap.set(rr.resourceUuid, rr);
    });

    const typeResources = resources.filter(r => String(r.resourceTypeUuid) === String(reqResourceTypeId));
    quantityMapRef.current = new Map(
      typeResources.map(r => [r.uuid!, existingMap.get(String(r.uuid))?.quantity ?? 1])
    );
    setGridRows(typeResources.map(res => ({ resource: res })));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally only on type/resource-list change, not on form sync
  }, [reqResourceTypeId, resources]);

  const handleDataBound = () => {
    const grid = gridRef.current;
    if (!grid) return;
    const existing = initialResourcesRef.current;
    const existingIds = new Set(existing.map(rr => String(rr.resourceUuid)).filter(Boolean));
    const selectedIndices = gridRows
      .map((row, i) => (row.resource.uuid && existingIds.has(String(row.resource.uuid)) ? i : -1))
      .filter(i => i >= 0);
    setTimeout(() => {
      try {
        grid.clearSelection();
        if (selectedIndices.length > 0) grid.selectRows(selectedIndices);
        // Update selectedUuids so templates re-render with correct enabled state
        setSelectedUuids(new Set(
          gridRows
            .filter((row, i) => selectedIndices.includes(i) && row.resource.uuid)
            .map(row => row.resource.uuid!)
        ));
      } catch { /* grid may be destroyed */ }
    }, 0);
  };

  const syncToForm = () => {
    const grid = gridRef.current;
    if (!reqResourceTypeId) return;
    const selected = (grid?.getSelectedRecords() ?? []) as { resource: ResourceDto }[];
    const selectedUuidSet = new Set(selected.map(r => r.resource.uuid!).filter(Boolean));
    setSelectedUuids(selectedUuidSet);
    const resourcesToSave = selected
      .filter(r => r.resource.uuid)
      .map(r => ({
        resourceUuid: r.resource.uuid!,
        quantity: quantityMapRef.current.get(r.resource.uuid!) ?? 1,
      }));
    setValue('resourceRequirement', { resourceTypeUuid: reqResourceTypeId, resources: resourcesToSave }, { shouldDirty: true });
  };

  const handleResourceTypeChange = (resourceTypeId: string | undefined) => {
    initialResourcesRef.current = [];
    setSelectedUuids(new Set());
    setValue('resourceRequirement', resourceTypeId ? { resourceTypeUuid: resourceTypeId, resources: [] } : undefined, { shouldDirty: true });
    setGridRows([]);
  };

  const handleQuantityChange = (resourceUuid: string, quantity: number) => {
    quantityMapRef.current.set(resourceUuid, quantity);
    syncToForm();
  };

  if (resourceTypesForRequirement.length === 0) return null;

  return (
    <div className="mt-6 pt-4 border-t border-gray-200">
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {t('processingLineSetupTimes.form.resourceRequirement', 'Ressourcenbedarf für Rüstzeit')}
      </label>
      <p className="text-xs text-gray-500 mb-2">
        {t('processingLineSetupTimes.form.resourceRequirementHint', 'Optional: Personen oder andere Ressourcen, die während der Rüstzeit benötigt werden.')}
      </p>
      <div className="mb-2">
        <DropDownListComponent
          dataSource={resourceTypesForRequirement.map(rt => ({ text: rt.name || '', value: rt.uuid || '' }))}
          fields={{ text: 'text', value: 'value' }}
          placeholder={t('processingLineSetupTimes.form.resourceRequirementResourceType', 'Ressourcentyp (z.B. Personal)')}
          floatLabelType="Never"
          value={reqResourceTypeId || ''}
          change={(e: { value?: string }) => handleResourceTypeChange(e.value)}
        />
      </div>
      {reqResourceTypeId && (
        <div className="h-48">
          <GridComponent
            ref={gridRef}
            locale="de-DE"
            dataSource={gridRows}
            height="100%"
            allowSorting={true}
            allowFiltering={false}
            selectionSettings={{ type: 'Multiple' }}
            dataBound={handleDataBound}
            rowSelected={syncToForm}
            rowDeselected={syncToForm}
          >
            <ColumnsDirective>
              <ColumnDirective type="checkbox" width={50} />
              <ColumnDirective
                field="resource.name"
                headerText={t('form.resource', 'Ressource')}
                template={(props: any) => {
                  const item = props.data || props.rowData || props;
                  return <span>{item?.resource?.name ?? ''}</span>;
                }}
              />
              <ColumnDirective
                headerText={t('routing.form.quantity', 'Menge')}
                width={130}
                template={(props: any) => {
                  const item = props.data || props.rowData || props;
                  if (!item?.resource?.uuid) return null;
                  const isSelected = selectedUuids.has(item.resource.uuid);
                  const uuid = item.resource.uuid as string;
                  return (
                    <input
                      type="number"
                      min={0.01}
                      step={0.01}
                      disabled={!isSelected}
                      defaultValue={quantityMapRef.current.get(uuid) ?? 1}
                      className="w-full border border-gray-300 rounded px-2 py-0.5 text-sm disabled:opacity-40 disabled:cursor-not-allowed"
                      onBlur={e => {
                        const v = parseFloat(e.target.value);
                        if (!isNaN(v) && v >= 0.01) handleQuantityChange(uuid, v);
                      }}
                    />
                  );
                }}
              />
            </ColumnsDirective>
            <Inject services={[Sort]} />
          </GridComponent>
        </div>
      )}
    </div>
  );
};

const processingLineSetupTimeSchema = z.object({
  resourceTypeUuid: z.string().min(1, 'validation.required'),
  resourceUuid: z.string().min(1, 'validation.required'),
  productUuid: z.string().min(1, 'validation.required'),
  setupTimeSeconds: z.union([z.number().int().min(0, 'validation.minValue'), z.null()]).optional(),
  resourceRequirement: z.object({
    resourceTypeUuid: z.string().optional(),
    resources: z.array(z.object({
      resourceUuid: z.string().optional(),
      quantity: z.number().positive()
    })).optional()
  }).optional().nullable(),
});

type ProcessingLineSetupTimeFormFieldsProps = {
  value: ProcessingLineSetupTimeFormState | null;
  resourceTypes: ResourceTypeDto[];
  allResourceTypes: ResourceTypeDto[];
  resources: ResourceDto[];
  products: MaterialDto[];
  formState: FormState<ProcessingLineSetupTimeFormState>;
  watch: UseFormWatch<ProcessingLineSetupTimeFormState>;
  setValue: UseFormSetValue<ProcessingLineSetupTimeFormState>;
  trigger: UseFormTrigger<ProcessingLineSetupTimeFormState>;
  t: TFunction;
};

const ProcessingLineSetupTimeFormFields: React.FC<ProcessingLineSetupTimeFormFieldsProps> = ({
  value,
  resourceTypes,
  allResourceTypes,
  resources,
  products,
  formState,
  watch,
  setValue,
  t
}) => {
  const { errors } = formState;
  const currentResourceTypeUuid = watch('resourceTypeUuid');
  const [localResources, setLocalResources] = React.useState<ResourceDto[]>([]);
  const [localLoading, setLocalLoading] = React.useState(false);

  // When editing: derive resourceTypeUuid from the loaded resource,
  // in case the API response does not return the field.
  React.useEffect(() => {
    const resourceUuid = watch('resourceUuid');
    if (value?.uuid && !currentResourceTypeUuid && resourceUuid && resources.length > 0) {
      const resource = resources.find(r => r.uuid === resourceUuid);
      if (resource?.resourceTypeUuid) {
        setValue('resourceTypeUuid', resource.resourceTypeUuid, { shouldValidate: false });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resources, value?.uuid]);

  React.useEffect(() => {
    const loadResources = async () => {
      if (!currentResourceTypeUuid) {
        setLocalResources([]);
        return;
      }

      setLocalLoading(true);
      try {
        const allResources = await ResourceControllerService.findAll();
        const filteredResources = allResources.filter(r => r.resourceTypeUuid === currentResourceTypeUuid);
        setLocalResources(filteredResources);
      } catch (error) {
        console.error('Error loading resources:', error);
      } finally {
        setLocalLoading(false);
      }
    };
    loadResources();
  }, [currentResourceTypeUuid]);

  return (
    <>
      <div className="mb-4">
        <DropDownListComponent
          dataSource={resourceTypes.map(rt => ({
            text: rt.name || '',
            value: rt.uuid || ''
          }))}
          fields={{ text: 'text', value: 'value' }}
          placeholder={t('processingLineSetupTimes.form.resourceType') + ' *'}
          floatLabelType="Never"
          cssClass={errors.resourceTypeUuid ? 'e-error' : ''}
          value={currentResourceTypeUuid || ''}
          enabled={!value?.uuid}
          change={(e: any) => {
            if (!e.isInteracted) return;
            setValue('resourceTypeUuid', e.value || undefined, { shouldValidate: false });
            setValue('resourceUuid', undefined, { shouldValidate: false });
          }}
        />
        {errors.resourceTypeUuid && (
          <div className="text-red-500 text-sm mt-1">
            {errors.resourceTypeUuid.message?.startsWith('validation.') 
              ? t(errors.resourceTypeUuid.message)
              : errors.resourceTypeUuid.message === 'Expected string, received null' || errors.resourceTypeUuid.message?.includes('null')
              ? t('validation.required')
              : errors.resourceTypeUuid.message}
          </div>
        )}
      </div>

      <div className="mb-4">
        <DropDownListComponent
          dataSource={localResources.map(r => ({
            text: r.name || '',
            value: r.uuid || ''
          }))}
          fields={{ text: 'text', value: 'value' }}
          placeholder={t('processingLineSetupTimes.form.resource') + ' *'}
          floatLabelType="Never"
          cssClass={errors.resourceUuid ? 'e-error' : ''}
          value={watch('resourceUuid') || ''}
          enabled={!!currentResourceTypeUuid && !localLoading}
          change={(e: any) => {
            if (e.isInteracted && e.value) {
              setValue('resourceUuid', e.value, { shouldValidate: false });
            }
          }}
        />
        {errors.resourceUuid && (
          <div className="text-red-500 text-sm mt-1">
            {errors.resourceUuid.message?.startsWith('validation.') 
              ? t(errors.resourceUuid.message)
              : errors.resourceUuid.message === 'Expected string, received null' || errors.resourceUuid.message?.includes('null')
              ? t('validation.required')
              : errors.resourceUuid.message}
          </div>
        )}
      </div>

      <div className="mb-4">
        <DropDownListComponent
          dataSource={products.map(p => ({
            text: p.name || '',
            value: p.uuid || ''
          }))}
          fields={{ text: 'text', value: 'value' }}
          placeholder={t('processingLineSetupTimes.form.product') + ' *'}
          floatLabelType="Never"
          cssClass={errors.productUuid ? 'e-error' : ''}
          value={watch('productUuid') || ''}
          enabled={!value?.uuid}
          change={(e: any) => {
            setValue('productUuid', e.value || undefined, { shouldValidate: false });
          }}
        />
        {errors.productUuid && (
          <div className="text-red-500 text-sm mt-1">
            {errors.productUuid.message?.startsWith('validation.') 
              ? t(errors.productUuid.message)
              : errors.productUuid.message === 'Expected string, received null' || errors.productUuid.message?.includes('null')
              ? t('validation.required')
              : errors.productUuid.message}
          </div>
        )}
      </div>

      <div className="mb-4">
        <label className="block text-sm font-medium mb-1">
          {t('processingLineSetupTimes.form.setupTime')}
        </label>
        <DurationInput
          value={watch('setupTimeSeconds') ?? undefined}
          onChange={(value) => {
            setValue('setupTimeSeconds', value ?? undefined, { shouldValidate: false, shouldDirty: true });
          }}
          required={false}
        />
        {errors.setupTimeSeconds && (
          <div className="text-red-500 text-sm mt-1">
            {errors.setupTimeSeconds.message?.startsWith('validation.') 
              ? t(errors.setupTimeSeconds.message)
              : errors.setupTimeSeconds.message === 'Expected number, received null' || errors.setupTimeSeconds.message?.includes('null')
              ? t('validation.required')
              : errors.setupTimeSeconds.message}
          </div>
        )}
      </div>

      <SetupResourceRequirementBlock
        watch={watch}
        setValue={setValue}
        allResourceTypes={allResourceTypes}
        resources={resources}
        t={t}
      />
    </>
  );
};

const ProcessingLineSetupTimeFormDialog: React.FC<CrudFormProps<ProcessingLineSetupTimeWithId>> = ({
  value,
  onClose,
  onSave
}) => {
  const { t } = useTranslation();
  const [resourceTypes, setResourceTypes] = React.useState<ResourceTypeDto[]>([]);
  const [allResourceTypes, setAllResourceTypes] = React.useState<ResourceTypeDto[]>([]);
  const [resources, setResources] = React.useState<ResourceDto[]>([]);
  const [products, setProducts] = React.useState<MaterialDto[]>([]);

  React.useEffect(() => {
    const load = async () => {
      try {
        const [allRT, allRes, allMat] = await Promise.all([
          ResourceTypeControllerService.findAll(),
          ResourceControllerService.findAll(),
          MaterialControllerService.findAll()
        ]);
        setAllResourceTypes(allRT ?? []);
        setResourceTypes((allRT ?? []).filter(rt => rt.category === 'PROCESSING_LINE'));
        setResources(allRes ?? []);
        setProducts((allMat ?? []).filter(m => m.materialType === 'PRODUCT'));
      } catch (error) {
        console.error('Error loading data:', error);
      }
    };
    load();
  }, []);

  // External (CrudFormProps) uses DTO shape; internal form state is form shape.
  // Cast at the boundary so react-hook-form operations type-check internally while
  // the CrudPage contract stays DTO-shaped.
  const formValue = value as unknown as ProcessingLineSetupTimeFormState | null;
  const formOnSave = onSave as unknown as (v: ProcessingLineSetupTimeFormState) => void | Promise<void>;

  return (
    <FormDialog
      value={formValue}
      onClose={onClose}
      onSave={formOnSave}
      titleKey="dialogs.processingLineSetupTime"
      width="600px"
      resolver={zodResolver(processingLineSetupTimeSchema) as unknown as Resolver<ProcessingLineSetupTimeFormState>}
    >
      {({ formState, watch, setValue, trigger }) => (
        <ProcessingLineSetupTimeFormFields
          value={formValue}
          resourceTypes={resourceTypes}
          allResourceTypes={allResourceTypes}
          resources={resources}
          products={products}
          formState={formState as FormState<ProcessingLineSetupTimeFormState>}
          watch={watch as UseFormWatch<ProcessingLineSetupTimeFormState>}
          setValue={setValue as UseFormSetValue<ProcessingLineSetupTimeFormState>}
          trigger={trigger as UseFormTrigger<ProcessingLineSetupTimeFormState>}
          t={t}
        />
      )}
    </FormDialog>
  );
};

export default ProcessingLineSetupTimeFormDialog;
